import Link from 'components/Basic/Link/Link';
import SimpleLayout from 'components/Layout/SimpleLayout/SimpleLayout';
import { ON_CONFIRM_REDIRECT_DELAY_MS } from 'config/constants';
import { getUserFriendlyErrors } from 'connectors/lib/friendlyErrorMessageParser';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { useConfirmEmailMutationApi } from 'graphql/generated';
import { canUseDom } from 'helpers/canUseDom';
import { removeSocialLoginDataCookie } from 'helpers/cookies/removeSocialLoginData';
import { getStringFromUrlQuery } from 'helpers/parsing/urlParsing';
import { dispatchBroadcastChannel } from 'hooks/useBroadcastChannel';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { BreadcrumbItemType } from 'types/breadcrumb';
import { setTokensToCookie } from 'utils/Auth/TokensFromCookies';
import { getInternationalizedStaticUrls } from 'utils/getInternationalizedStaticUrls';

type ConfirmAccountProps = {
    breadcrumbs: BreadcrumbItemType[];
};

export const ConfirmAccount: FC<ConfirmAccountProps> = ({ breadcrumbs }) => {
    const { t } = useTranslation();
    const { url: domainUrl } = useDomainConfig();
    const { updateCartUuid, updateUserState, updateAuthLoadingState } = usePersistStore((s) => s);
    const router = useRouter();

    const [loginUrl, forgottenPasswordUrl] = getInternationalizedStaticUrls(['/login', '/reset-password'], domainUrl);

    const confirmEmailValue = getStringFromUrlQuery(router.query.email);
    const confirmHashValue = getStringFromUrlQuery(router.query.hash);
    const isConfirmationPageValid = confirmEmailValue !== '' && confirmHashValue !== '';

    const [confirmEmailResult, confirmEmail] = useConfirmEmailMutationApi();
    const { applicationError } = confirmEmailResult.error
        ? getUserFriendlyErrors(confirmEmailResult.error, t)
        : { applicationError: undefined };

    useEffect(() => {
        if (isConfirmationPageValid) {
            confirmEmail({
                email: confirmEmailValue,
                hash: confirmHashValue,
            }).then((result) => {
                if (result.data === undefined) {
                    return;
                }

                if (result.data.ConfirmEmail.signIn) {
                    const accessToken = result.data.ConfirmEmail.tokens.accessToken;
                    const refreshToken = result.data.ConfirmEmail.tokens.refreshToken;
                    const uuid = result.data.ConfirmEmail.uuid;
                    const email = result.data.ConfirmEmail.email;

                    updateUserState({ userUuid: uuid });
                    updateUserState({ userEmail: email });
                    updateCartUuid(null);
                    setTokensToCookie(accessToken, refreshToken);
                    removeSocialLoginDataCookie();
                    updateAuthLoadingState('login-loading');
                    dispatchBroadcastChannel({ name: 'reloadPage' });

                    setTimeout(() => {
                        if (canUseDom()) {
                            window.location.href = '/';
                        } else {
                            router.push('/').then(() => router.reload());
                        }
                    }, ON_CONFIRM_REDIRECT_DELAY_MS);
                }
            });
        }
    }, [
        isConfirmationPageValid,
        confirmEmailValue,
        confirmHashValue,
        confirmEmail,
        router,
        updateCartUuid,
        updateUserState,
    ]);

    return (
        <SimpleLayout breadcrumb={breadcrumbs} heading={t('Tescoma Club account registration')}>
            <div className="text-lg block font-semibold">
                {confirmEmailResult.data === undefined &&
                    confirmEmailResult.error === undefined &&
                    t('Confirming your email address, just a second please...')}
                {confirmEmailResult.data !== undefined && t('Your account is now active!')}

                {applicationError?.message &&
                    (applicationError.type === 'already-confirmed-account' ? (
                        <Link href={loginUrl}>{applicationError.message}</Link>
                    ) : applicationError.type === 'invalid-reset-password-hash' ? (
                        <Link href={forgottenPasswordUrl}>{applicationError.message}</Link>
                    ) : (
                        applicationError.message
                    ))}
            </div>
        </SimpleLayout>
    );
};
